import React, { useRef } from 'react';
import { Box, Button, Typography, Chip } from '@mui/material';
import { useField, useFormikContext } from 'formik';
import { IHubspotField } from '../../../../../models/hubspot-forms';

type FileArray = File[];

interface FileFieldAdapterProps {
  field: IHubspotField;
  /**
   * Optional accept filter. If omitted, defaults to images and videos.
   * Example: "image/*,video/*,application/pdf"
   */
  accept?: string;
  /**
   * Optional max files override for multi-file fields (default: 10).
   * Ignored for single-file fields (implicitly 1).
   */
  maxFiles?: number;
  /**
   * Optional max file size in MB
   */
  maxFileSizeMB?: number;
}

export const FileFieldAdapter: React.FC<FileFieldAdapterProps> = ({
  field,
  accept = 'image/*,video/*',
  maxFiles = 10,
  maxFileSizeMB = 25
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { setFieldValue, setFieldTouched, validateField } = useFormikContext<any>();
  const [formikField, meta] = useField<FileArray>(field.name);

  if (field.hidden) return null;

  const isMultiple = true; // Hubspot API is bugged, so hardcoding for now
  const files: FileArray = Array.isArray(formikField.value) ? formikField.value : [];

  const showError = meta.touched && Boolean(meta.error);

  const handleOpen = () => {
    inputRef.current?.click();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.currentTarget.files ? Array.from(e.currentTarget.files) : [];
    if (selected.length === 0) return;

    let next: FileArray;

    if (isMultiple) {
      // Append new files to the current selection (dedupe by name+size+lastModified)
      const existingKeys = new Set(files.map(f => fileKey(f)));
      const deduped = selected.filter(f => !existingKeys.has(fileKey(f)));
      next = [...files, ...deduped].slice(0, maxFiles);
    } else {
      // Single-file: replace with the first selected file (as an array of length 1)
      next = selected.slice(0, 1);
    }

    setFieldValue(field.name, next);
    setFieldTouched(field.name, true);
    // Trigger validation to show any errors immediately
    setTimeout(() => validateField(field.name), 0);

    // Reset the input value so the same file can be re-selected if needed
    e.currentTarget.value = '';
  };

  const handleRemove = (index: number) => {
    const next = files.filter((_, i) => i !== index);
    setFieldValue(field.name, next);
    setFieldTouched(field.name, true);
    // Trigger validation to clear any errors if the remaining files are valid
    setTimeout(() => validateField(field.name), 0);
  };

  const handleClear = () => {
    setFieldValue(field.name, []);
    setFieldTouched(field.name, true);
    // Trigger validation to clear any errors since empty arrays should pass validation
    setTimeout(() => validateField(field.name), 0);
  };

  return (
    <Box sx={{ my: 1 }}>
      <Typography
        sx={{ display: 'block', mb: 1, fontWeight: 600 }}
        dangerouslySetInnerHTML={{ __html: field.label || field.name }}
      />

      <input
        ref={inputRef}
        id={field.name}
        name={field.name}
        type="file"
        style={{ display: 'none' }}
        accept={accept}
        multiple={isMultiple}
        onChange={handleChange}
        onBlur={() => setFieldTouched(field.name, true)}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: 1
        }}
      >
        <Button
          variant="contained"
          onClick={handleOpen}
          sx={{
            width: { xs: '100%', sm: 'auto' },
            mb: { xs: 1, sm: 0 }
          }}
        >
          {isMultiple ? 'Choose files' : 'Choose file'}
        </Button>
        {isMultiple && files.length > 0 && (
          <Button
            variant="text"
            onClick={handleClear}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              mb: { xs: 1, sm: 0 }
            }}
          >
            Clear all
          </Button>
        )}
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            alignSelf: { xs: 'center', sm: 'auto' },
            mb: { xs: 1, sm: 0 }
          }}
        >
          {isMultiple ? `Up to ${maxFiles} files` : ''}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          mt: 1
        }}
      >
        {files.map((f, i) => (
          <Chip
            key={`${f.name}-${f.size}-${f.lastModified}`}
            label={f.name}
            onDelete={() => handleRemove(i)}
            sx={{
              maxWidth: 200,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          />
        ))}
      </Box>
      {/* Formik/Yup error (string) or array/object of errors */}
      {showError && (
        <Typography variant="caption" color="error">
          {normalizeError(meta.error)}
        </Typography>
      )}
    </Box>
  );
};

function fileKey(f: File): string {
  return `${f.name}-${f.size}-${f.lastModified}`;
}

function normalizeError(err: unknown): string {
  if (typeof err === 'string') return err;
  if (Array.isArray(err)) return err.filter(Boolean).join(', ');
  if (err && typeof err === 'object' && 'message' in (err as any))
    return String((err as any).message);
  return 'Invalid file selection';
}
